import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  /*
   * Fix v3 (28/07/2026 · dopo report bloccante utente):
   * La logica precedente usava `p-0` come opt-out del wrap. Ma `CaseDialog`
   * (Nuova pratica) usa `p-0 overflow-hidden` + DialogHeader/DialogFooter →
   * il body non scrollava e header/footer superavano l'altezza schermo.
   *
   * Nuova regola:
   *   Se children contiene DialogHeader O DialogFooter → SEMPRE structured
   *     layout (flex-col, header/footer fissi, body scroll interno), a
   *     prescindere da `p-0` nella className del consumer.
   *   Se children non contiene né DialogHeader né DialogFooter → layout
   *     legacy grid, tutto il DialogContent scrollabile come fallback safe.
   *
   * Rimosso il ramo speciale di opt-out via `p-0`: era la causa del bug.
   */
  const childArr = React.Children.toArray(children).filter(Boolean);
  let header = null;
  let footer = null;
  const bodyParts = [];
  for (const c of childArr) {
    if (c.type === DialogHeader) header = c;
    else if (c.type === DialogFooter) footer = c;
    else bodyParts.push(c);
  }
  const hasStructuredParts = Boolean(header || footer);

  if (!hasStructuredParts) {
    // Fallback: nessun DialogHeader/DialogFooter → l'intero DialogContent
    // diventa scrollabile per garantire che nessun contenuto sia irraggiungibile.
    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (e.detail && e.detail.originalEvent && e.detail.originalEvent.type !== "keydown") {
              e.preventDefault();
            }
          }}
          className={cn(
            "fixed inset-4 z-50 grid w-auto max-w-[42rem] mx-auto my-auto gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg",
            className,
            "max-h-[calc(100dvh-2rem)] !overflow-y-auto !overflow-x-hidden"
          )}
          {...props}>
          {children}
          <DialogPrimitive.Close
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10 bg-background/80 backdrop-blur">
            <X className="h-4 w-4" />
            <span className="sr-only">Chiudi</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }

  // Consumer normalizzato con DialogHeader/DialogFooter:
  // Rimuovo eventuali override consumer che impediscono lo scroll interno:
  //  - "overflow-hidden": annullato con !overflow-hidden dopo, non serve rimuoverlo
  //    perché forziamo il flex-col con overflow-hidden sul Content e overflow-y-auto sul body
  //  - "p-0": rimosso dalla className passata al Content perché il padding è ora
  //    gestito internamente da questo wrapper (px-6 su header/body/footer)
  const cleanClassName = typeof className === "string"
    ? className.replace(/(^|\s)p-0(\s|$)/g, " ").replace(/(^|\s)gap-0(\s|$)/g, " ").trim()
    : className;

  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      /*
       * Click-outside bloccato per protezione data-entry. Escape lasciato
       * abilitato (default Radix) come richiesto: l'operatore deve sempre
       * poter uscire dal modale con Esc.
       */
      onPointerDownOutside={(e) => e.preventDefault()}
      onInteractOutside={(e) => {
        // Consenti Escape (mantiene default Radix), blocca solo pointer/touch outside.
        if (e.detail && e.detail.originalEvent && e.detail.originalEvent.type !== "keydown") {
          e.preventDefault();
        }
      }}
      className={cn(
        "fixed inset-4 z-50 flex flex-col w-auto max-w-[42rem] mx-auto my-auto border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg",
        "max-h-[calc(100dvh-2rem)] overflow-hidden",
        cleanClassName
      )}
      {...props}>
      {header && (
        <div className="shrink-0 px-6 pt-6 pb-2">{header}</div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-2">
        {bodyParts}
      </div>
      {footer && (
        <div className="shrink-0 px-6 pt-2 pb-6 border-t bg-background">{footer}</div>
      )}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10 bg-background/80 backdrop-blur">
        <X className="h-4 w-4" />
        <span className="sr-only">Chiudi</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
